import { FormikErrors } from 'formik';
import { useState } from 'react';

import { CustomerGroupSettingsMessage } from '../../../../../../types/CustomerGroupSettingsMessage';
import { UserRole } from '../../../../../../types/UserRole';
import { Button } from '../../../../../cdl/Button/Button';
import { Modal } from '../../../../../cdl/Modal/Modal';
import { Switch } from '../../../../../cdl/Switch/Switch';
import { Text } from '../../../../../cdl/Text/Text';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { translate } from '../../../../../common/helpers/translate.helper';
import { Box } from '../../../../../common/ui/Box';
import { companyAccessLevel, groupAccessLevel } from '../../../../../user/list/utils/UserAccessLevels';
import { SsoGroupForm, SsoGroupFormValues } from '../../../../components/SsoGroupForm';
import { useCustomerGroupSettings } from '../../hooks/useCustomerGroupSettings';
import { useCustomerGroupSettingsUpdate } from '../../hooks/useCustomerGroupSettingsUpdate';

interface CustomerCreateSsoGroupModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    groupId: string;
}

export const CustomerCreateSsoGroupModal = ({ isOpen, onDismiss, groupId }: CustomerCreateSsoGroupModalProps) => {
    const settingsQuery = useCustomerGroupSettings(groupId);
    const updateMutation = useCustomerGroupSettingsUpdate(groupId);
    const { addToast, addErrorToast } = useToasts();
    const [createMore, setCreateMore] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    const initialValues: SsoGroupFormValues = {
        groupName: '',
        role: UserRole.GROUP_ADMIN,
        companyIds: [],
    };

    const validate = (values: SsoGroupFormValues): FormikErrors<SsoGroupFormValues> => {
        const errors: FormikErrors<SsoGroupFormValues> = {};

        if (!values.groupName || values.groupName.trim().length === 0) {
            errors.groupName = translate('sso.groupNameRequired');
        } else if (!/^[a-zA-Z0-9\s_-]+$/.test(values.groupName.trim())) {
            errors.groupName = translate('sso.groupNameInvalidCharacters');
        } else if (settingsQuery.data?.ssoConfiguration?.samlGroupMapping[values.groupName.trim()]) {
            errors.groupName = translate('sso.groupNameDuplicate');
        }

        if (!values.role) {
            errors.role = translate('sso.roleRequired');
        }

        if (values.role !== UserRole.GROUP_ADMIN && !values.companyIds?.length) {
            errors.companyIds = translate('user.error.requiredCompanyIds');
        }

        return errors;
    };

    const handleSubmit = async (values: SsoGroupFormValues, { resetForm }: { resetForm: () => void }) => {
        if (!settingsQuery.data?.ssoConfiguration || !values.role) return;

        const trimmedGroupName = values.groupName.trim();
        const newGroup = {
            accessLevel: groupAccessLevel[values.role],
            companyAccesses:
                values.role === UserRole.GROUP_ADMIN
                    ? undefined
                    : values.companyIds.map((companyId) => ({
                          companyId,
                          accessLevel: companyAccessLevel[values.role!],
                      })),
        };

        const updatedSettings: CustomerGroupSettingsMessage = {
            ...settingsQuery.data,
            ssoConfiguration: {
                ...settingsQuery.data.ssoConfiguration,
                samlGroupMapping: {
                    ...settingsQuery.data.ssoConfiguration.samlGroupMapping,
                    [trimmedGroupName]: newGroup,
                },
            },
        };

        try {
            await updateMutation.mutateAsync({
                groupId,
                settings: updatedSettings,
            });
            addToast(translate('sso.createGroupSuccess', { groupName: trimmedGroupName }));

            resetForm();
            if (createMore) {
                setResetKey((prev) => prev + 1);
            } else {
                onDismiss();
                setCreateMore(false);
            }
        } catch {
            addErrorToast(translate('sso.createGroupError'));
        }
    };

    const handleDismiss = () => {
        setCreateMore(false);
        onDismiss();
    };

    return (
        <Modal isOpen={isOpen} onDismiss={handleDismiss}>
            <Box display="flex" flexDirection="column" gap={24} padding={24}>
                <Box display="flex" flexDirection="column" gap={8}>
                    <Text variant="subtitle1" as="h2">
                        {translate('sso.createGroupModal.title')}
                    </Text>
                    <Text variant="body" color="foreground.subtle">
                        {translate('sso.createGroupModal.description')}
                    </Text>
                </Box>

                <SsoGroupForm
                    groupId={groupId}
                    groupType="customer"
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validate={validate}
                    resetKey={resetKey}
                >
                    {({ isValid, dirty }) => (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Switch
                                checked={createMore}
                                onChange={(checked) => setCreateMore(checked)}
                                label={translate('sso.createMoreLabel')}
                            />
                            <Box display="flex" gap={8}>
                                <Button type="button" emphasis="medium" onClick={handleDismiss}>
                                    {translate('btn.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    emphasis="high"
                                    disabled={!isValid || !dirty}
                                    isLoading={updateMutation.isPending}
                                >
                                    {translate('sso.createGroupButton')}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </SsoGroupForm>
            </Box>
        </Modal>
    );
};
